<template lang="pug">
.cookie-consent(v-if="alertNeeded")
  .cookie-consent__alert(v-if="!customizing")
    h1 We value your privacy
    p We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. See our&nbsp;
      landing-link(path="/privacy" title="Privacy Policy" target="_blank") Privacy Policy
      | &nbsp;for details.
    .cookie-consent__actions
      el-button(type="primary" size="small" @click="acceptAll") Accept all
      el-button(type="primary" size="small" @click="rejectAll" v-if="rejectable") Reject all
      el-button(size="small" @click="customizing = true") Customize

  el-dialog.cookie-consent__dialog(
    title="Customize Consent Preferences"
    :visible.sync="customizing")
    .cookie-consent__info
      p We use cookies to help you navigate efficiently and perform certain functions. You will find detailed information about all cookies under each consent category below.
      p The cookies that are categorized as "Necessary" are stored on your browser as they are essential for enabling the basic functionalities of the site.
      p We also use third-party cookies that help us analyze how you use this website, store your preferences, and provide the content and advertisements that are relevant to you.
      p These cookies will only be stored in your browser with your prior consent.
      p You can choose to enable or disable some or all of these cookies but disabling some of them may affect your browsing experience.
    .cookie-consent__table
      .cookie-consent__category(
        v-for="i, k in categories"
        :key="k"
        :class="{ 'cookie-consent__category--expanded': !!expanded[k] }"
        @click="toggle(k)")
        .cookie-consent__category__header
          h3.cookie-consent__category__title {{ i.title }}
          .cookie-consent__category__locked(v-if="i.locked") Always active
          .cookie-consent__category__toggle(@click.stop)
            el-switch(v-model="state[k]" :disabled="i.locked")
        .cookie-consent__category__description {{ i.description }}
        .cookie-consent__cookies(v-if="expanded[k]")
          dl.cookie-consent__cookie(v-for="j, m in i.cookies" :key="m")
            dt Cookie
            dd.cookie-consent__cookie-name {{ j.name }}
            dt Duration
            dd.cookie-consent__cookie-expires {{ j.expires }}
            dt Description
            dd.cookie-consent__cookie-description {{ j.description }}

    .cookie-consent__actions(slot="footer")
      el-button(@click="save") Save
      el-button(type="primary" @click="acceptAll") Accept all
      el-button(type="primary" @click="rejectAll" v-if="rejectable") Reject all
</template>

<script>
import { BUILD_MODE, COOKIE_OPTIONS, COOKIE_CONSENT_CATEGORIES } from '~/settings'

export default {
  props: {
    rejectable: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      alertNeeded: false,
      customizing: false,
      expanded: {},
      categories: COOKIE_CONSENT_CATEGORIES,
      state: {
        ...Object.keys(COOKIE_CONSENT_CATEGORIES)
          .reduce((res, key) => ({
            ...res,
            [key]: COOKIE_CONSENT_CATEGORIES[key].locked || false
          }), {}),
        created: Date.now(),
        confirmed: false
      }
    }
  },

  computed: {
    consentCookieKey () {
      return `consent_${BUILD_MODE}`
    }
  },

  mounted () {
    this.sync()
  },

  methods: {
    sync () {
      this.alertNeeded = !this.$cookies.get(this.consentCookieKey)
    },

    toggle (k) {
      this.$set(this.expanded, k, !this.expanded[k])
    },

    close () {
      this.customizing = false
    },

    save () {
      this.persist()
      this.close()
    },

    rejectAll () {
      this.state = {
        ...this.state,
        ...Object
          .keys(COOKIE_CONSENT_CATEGORIES)
          .reduce((res, key) => ({
            ...res,
            [key]: COOKIE_CONSENT_CATEGORIES[key].locked || false
          }), {})
      }
      this.persist()
      this.close()
    },

    acceptAll () {
      this.state = {
        ...this.state,
        ...Object
          .keys(COOKIE_CONSENT_CATEGORIES)
          .reduce((res, key) => ({
            ...res,
            [key]: true
          }), {})
      }
      this.persist()
      this.close()
    },

    persist () {
      this.$set(this.state, 'confirmed', Date.now())
      this.$cookies.set(this.consentCookieKey, this.state, COOKIE_OPTIONS)
      this.sync()
      this.$workflow.validateCookieConsent()
    }
  }
}
</script>

<style lang="scss" scoped>
.cookie-consent {
  &__dialog {
    user-select: none;
    ::v-deep() {
      .el-dialog {
        border-radius: $--radius-lg;
        &__body {
          padding-top: 16px;
          padding-bottom: 0;
        }
      }
    }
  }
  &__alert {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    background: rgba(black, .9);
    padding: 16px;
    text-align: left;
    z-index: 20;
    line-height: 135%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-top: $--border-light;
    color: white;
    backdrop-filter: blur(16px);
    @include media(sm) {
      max-width: 326px;
      bottom: 20px;
      right: 20px;
      left: initial;
      border-top: none;
      border-radius: $--radius-lg;
      box-shadow: $--box-shadow-modern;
    }
    h1 {
      font-size: 18px;
      font-weight: normal;
      margin: 0;
    }
    p {
      font-size: 13px;
      color: lighten($--color-text-secondary, 15);
      margin: 0;
      a {
        color: lighten($--color-primary, 30);
      }
    }
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0 0 24px;
    p {
      font-size: 13px;
      word-break: break-word;
      line-height: 135%;
      color: $--color-text-secondary;
      margin: 0;
    }
  }
  &__table {
    word-break: break-word;
  }
  &__category {
    border-top: $--border-light;
    padding: 16px 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    cursor: pointer;
    &:before {
      content: '';
      position: absolute;
      top: 16px;
      left: -4px;
      width: 20px;
      height: 20px;
      background-size: contain;
      background-image: url("data:image/svg+xml,%3csvg width='24' height='24' fill='currentColor' viewBox='0 0 24 24' transform='' xmlns='http://www.w3.org/2000/svg'%3e%3c!--Boxicons v3.0.8 https://boxicons.com %7c License https://docs.boxicons.com/free--%3e%3cpath d='m12 15.41 5.71-5.7-1.42-1.42-4.29 4.3-4.29-4.3-1.42 1.42z'%3e%3c/path%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: center;
      transform: rotate(-90deg);
      transition: transform .2s ease-in-out;
    }
    &--expanded {
      &:before {
        transform: rotate(0);
      }
    }
    &__header {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    &__locked {
      font-weight: $--font-weight-bold;
      color: darken($--color-success, 5);
    }
    &__title {
      margin: 0;
      flex: 1;
      font-weight: normal;
    }
    &__description {
      font-size: 13px;
      line-height: 150%;
      color: $--color-text-secondary;
    }
  }
  &__cookies {
    background: darken(white, 2);
    border-radius: $--radius-lg;
    padding: 0 12px;
    overflow: hidden;
  }
  &__cookie {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 0 12px;
    margin: 0;
    padding: 12px 0;
    &:not(:first-child) {
      border-top: $--border-light;
    }
    dt {
      font-size: 12px;
      line-height: 20px;
      font-weight: $--font-weight-bold;
      min-width: 0;
    }
    dd {
      margin: 0;
      padding: 0;
      min-width: 0;
    }
    &-name {
      font-family: $--font-family-monospace;
      font-size: 13px;
      line-height: 150%;
    }
    &-description {
      font-size: 12px;
      line-height: 150%;
      color: $--color-text-secondary;
    }
    &-expires {
      font-size: 12px;
      line-height: 150%;
      color: $--color-text-secondary;
    }
  }
}
</style>
